import React from "react";
import { ResponsiveModal } from "@UI/Components/ResponsiveModal/ResponsiveModal";
import { Icon } from "@UI/Components/Icon/Icon";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { Box } from "@UI/Components/Box/Box";
import { Button } from "@UI/Components/Button/Button";
import useThemeToken from "@UI/hooks/useThemeToken";
import { List, ListItem, ListIcon } from "@UI/Components/List/List";
import { SuperTalentBadge } from "@UI/Components/Icons/SuperTalentBadge";
import { Bell } from "@UI/Components/Icons/Bell";
import { Medal } from "@UI/Components/Icons/Medal";
import { Suitcase } from "@UI/Components/Icons/Suitcase";
import messages from "./messages";
import { Link } from "@UI/Components/Link/Link";

interface Props {
  isOpen: boolean;
  handleConfirm: () => string;
  onClose: () => void;
  isMobile: boolean;
}

export const SupertTalentModal = ({
  isOpen,
  handleConfirm,
  onClose,
  isMobile,
}: Props) => {
  const pink500 = useThemeToken("colors", "pink.500");
  const orange500 = useThemeToken("colors", "orange.500");
  const pink600 = useThemeToken("colors", "pink.600");
  const orange600 = useThemeToken("colors", "orange.600");

  const advantages = [
    {
      title: "super_talent_advantage_one_title",
      subtitle: "super_talent_advantage_one_subtitle",
      icon: <ListIcon as={Bell} color="gray.700" marginTop="xs" />,
    },
    {
      title: "super_talent_advantage_two_title",
      subtitle: "super_talent_advantage_two_subtitle",
      icon: <ListIcon as={Medal} color="gray.700" marginTop="xs" />,
    },
    {
      title: "super_talent_advantage_three_title",
      subtitle: "super_talent_advantage_three_subtitle",
      icon: <ListIcon as={Suitcase} color="gray.700" marginTop="xs" />,
    },
  ];

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={() => onClose()}
      isCentered={!isMobile}
      size={{ base: "sm", md: "lg" }}
    >
      <Box>
        <Heading as="h2" marginRight="3" mb={2}>
          <Heading textAlign="start" fontSize={{ base: "2xl", lg: "4xl" }}>
            <FormattedMessage
              {...messages.superTalent}
              values={{
                superTalentBadge: () => (
                  <Icon
                    as={SuperTalentBadge}
                    boxSize={{ base: "4", lg: "6" }}
                    marginRight="7px"
                    marginBottom="6px"
                    display="inline"
                  />
                ),
              }}
            />
          </Heading>
        </Heading>
        <Text fontSize="md">
          <FormattedMessage {...messages.topProviderMessage} />
        </Text>
        <Box>
          <List spacing="1">
            {advantages.map((advantage, index) => (
              <ListItem
                key={`advantage-${index}`}
                display="flex"
                alignItems="center"
                mb={{ base: 2, md: 3 }}
              >
                <Box>{advantage.icon}</Box>
                <Box marginLeft="2">
                  <Box>
                    <Text as="span" fontWeight={700} color="#333" fontSize="md">
                      <FormattedMessage {...messages[advantage.title]} />
                    </Text>
                    <Box>
                      <Text as="span" fontSize="md">
                        <FormattedMessage {...messages[advantage.subtitle]} />
                      </Text>
                    </Box>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
        <Link as="a" href={handleConfirm()}>
          <Button
            colorScheme="pink"
            size="lg"
            width="full"
            mb={4}
            background={`linear-gradient(96.51deg, ${pink500} 0%, ${orange500} 99.87%);`}
            _hover={{
              background: `linear-gradient(96.51deg, ${pink600} 0%, ${orange600} 99.87%);`,
            }}
          >
            <FormattedMessage {...messages.discover} />
          </Button>
        </Link>
      </Box>
    </ResponsiveModal>
  );
};
